/* eslint-disable react/no-children-prop */
import { useEffect, useState } from 'react';
import { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Divider } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { Accordion, AccordionSubtitle, Modal } from 'src/components/molecules';
import { TagTypeTitle } from 'src/components/molecules/TagTypeTitle/TagTypeTitle';
import { deleteAllTagsAction } from 'src/components/pages/Tags/actions/deleteAllTagsAction';
import { updateTagTypeStatusAction } from 'src/components/pages/Tags/actions/updateTagTypeStatusAction';
import {
  OderContainer,
  OrderNumber,
  TagTypesJoiner,
} from 'src/components/pages/Tags/Tags.styles';
import {
  TagTypeType,
  useDeleteAllTagsMutation,
  useUpdateTagTypeStatusMutation,
} from 'src/graphql';
import { useWindowDimensions } from 'src/hooks';
import { AppDispatch, TRootState } from 'src/store';
import { useTheme } from 'styled-components';
import { TagTypeStatus } from 'src/types';
import { TagTypeBody } from '../TagTypeBody';
import {
  getTagTypeBorderColor,
  getTagTypeColor,
  getTagTypeHoverColor,
} from './helper';
import {
  DividerContainer,
  TagTypeContainer,
  TagTypeFooter,
} from './TagTypeItem.styles';
import { tagTypeItem } from './TagTypeItem.vm';

interface Props {
  index: number;
  type: TagTypeType;
  droppableId: string;
  dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
  recalculateCoordinates: () => void;
  tagTypeJoinerCoordinates: (number[] | null[])[];
  setRef: (element: HTMLElement | null, key: string) => void;
  isDragging?: boolean;
}

export const TagTypeItem = ({
  index,
  type: {
    id,
    colorId,
    statusUpdatedAt,
    status,
    name: title,
    Author,
    createdAt,
    Tags,
  },
  droppableId,
  dragHandleProps,
  isDragging,
  recalculateCoordinates,
  tagTypeJoinerCoordinates,
  setRef,
}: Props) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const theme = useTheme();

  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  const { mutateAsync: updateTagTypeStatusAsync, isLoading: isLoadingUpdate } =
    useUpdateTagTypeStatusMutation();
  const { mutateAsync: deleteAllTagsAsync, isLoading: isLoadingDelete } =
    useDeleteAllTagsMutation();

  const { hasNoTags, canActivateTagType } = useSelector((state: TRootState) =>
    tagTypeItem(state, id),
  );

  const mutationIsLoading = isLoadingUpdate || isLoadingDelete;
  const [x1, x2, y1, y2] = tagTypeJoinerCoordinates?.[index] ?? [
    null,
    null,
    null,
    null,
  ];
  const coordinatesDefined = x1 && x2 && y1 && y2;

  const updateTagTypeStatus = async () => {
    dispatch(
      updateTagTypeStatusAction({
        tagTypeId: id,
        status:
          status === TagTypeStatus.LIVE
            ? TagTypeStatus.PAUSED
            : TagTypeStatus.LIVE,
        mutationUpdateTagTypeStatus: updateTagTypeStatusAsync,
        queryClient,
      }),
    );
  };

  const handleClearAllTags = async (tagType: string) => {
    await dispatch(
      deleteAllTagsAction({
        tagType,
        mutationDeleteAllTags: deleteAllTagsAsync,
        queryClient,
      }),
    );
  };
  const { width, height } = useWindowDimensions();

  useEffect(() => {
    recalculateCoordinates();
  }, [width, height, recalculateCoordinates]);

  return (
    <TagTypeContainer>
      <OderContainer>
        <OrderNumber
          key={`${id}-priority`}
          ref={(element) => setRef(element, `${id}-priority`)}
          {...dragHandleProps}
        >
          {index + 1}
        </OrderNumber>
        {coordinatesDefined && (
          <TagTypesJoiner
            height="100%"
            width="100px"
            key={`${x1}${x2}${y1}${y2}`}
          >
            <line
              x1={x1}
              x2={x2}
              y1={y1}
              y2={y2}
              stroke={
                theme.isVaultVariant
                  ? theme.newPalette.turquoise.T300
                  : theme.atlas.semantic.color.action.primary.default
              }
              strokeWidth="1"
            />
          </TagTypesJoiner>
        )}
      </OderContainer>
      <Accordion
        data-testid={`tag-type-accordion-${id}`}
        title={<TagTypeTitle index={index} name={title} />}
        last={true}
        topBorder
        shadow
        topBorderRadius="12px"
        bottomBorderRadius="12px"
        defaultOpen={false}
        onClick={() => {}}
        headerProps={dragHandleProps}
        subtitle={
          status === TagTypeStatus.LIVE && (
            <AccordionSubtitle
              date={statusUpdatedAt ?? createdAt}
              author={Author?.displayName || Author?.name || 'Vault'}
              showCreatedLabel={false}
              id={id}
            />
          )
        }
        backgroundColor={getTagTypeColor(colorId)}
        bodyBackgroundColor={
          theme.isVaultVariant
            ? theme.newPalette.neutral.N0
            : theme.atlas.semantic.color.surface.default
        }
        borderColor={getTagTypeBorderColor(colorId)}
        hoverColor={getTagTypeHoverColor(colorId)}
        openCallback={recalculateCoordinates}
        isDragging={isDragging}
      >
        <TagTypeBody
          tagTypeId={id}
          tags={Tags ?? []}
          updateTagTypeMutationisLoading={mutationIsLoading}
          recalculateCoordinates={recalculateCoordinates}
          tagTypeName={title}
          droppableId={droppableId}
        />
        <DividerContainer>
          <Divider
            variant="horizontal"
            color={
              theme.isVaultVariant
                ? theme.newPalette.neutral.N300
                : theme.atlas.semantic.color.ui.divider.default
            }
          />
        </DividerContainer>
        <TagTypeFooter>
          <Button
            size="medium"
            variant="secondary"
            width="140px"
            disabled={mutationIsLoading || !!hasNoTags}
            onClick={() => setShowConfirmModal(true)}
            data-testid={`tag-type-accordion-clear-all-button-${id}`}
          >
            Clear all tags
          </Button>

          <Button
            variant="primary"
            size="medium"
            width="180px"
            noTextWrap
            onClick={updateTagTypeStatus}
            disabled={!canActivateTagType}
            data-testid={`tag-type-accordion-update-status-button-${id}`}
          >
            {status !== TagTypeStatus.LIVE ? (
              <>
                <StyledIconInButton
                  size="medium"
                  position="left"
                  className="uil uil-play"
                />
                Activate tag type
              </>
            ) : (
              <>
                <StyledIconInButton
                  size="medium"
                  position="left"
                  className="uil uil-pause"
                />
                Pause tag type
              </>
            )}
          </Button>
        </TagTypeFooter>
      </Accordion>
      <Modal
        size="medium"
        data-testid={`clear-all-tags-modal-${id}`}
        title="Are you sure you want to clear all tags in this tag type?"
        description={
          <>
            <span>
              This action will delete all tags within this tag type and cannot
              be undone. Deleting a tag will remove it from all cases where it
              has been applied.
            </span>
            <br></br>
            <br></br>
            <span>To confirm, press &quot;Clear all&quot;.</span>
          </>
        }
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={() => {
          setShowConfirmModal(false);
          handleClearAllTags(id);
        }}
        confirmButtonText="Clear All"
      />
    </TagTypeContainer>
  );
};
